import { isEmpty } from 'lodash';
import { Batch } from '@sda/sdk/lib/batch';
import { FirestoreInstanceTypes } from '@sda/enums';
import { firestoreDocumentExists } from '@sda/utils/store';
import { TransactionManager } from '../../lib/transaction-manager.helpers';
import { Transaction } from './transaction';
import type { PrivatePendingProfile, PublicPendingProfile } from '@sda/interfaces/profile';
import type { ShowNotificationEmail } from '@sda/interfaces/queue/shownotificationemail';
import type { Link } from '@sda/interfaces/link';
import type { BeaconNotificationEmail } from '@sda/interfaces/queue/beaconnotificationemail';
import type { ReviewNotificationEmail } from '@sda/interfaces/queue/reviewnotificationemail';
import type { Feedback } from '@sda/interfaces/feedback';
import type { GenericList } from '@sda/helpers/helpers.types';
import type {
  FetchDataType, FirestoreClientData, QueryObject, QueryOptions,
  SetDataType, Sitemap,
} from '../../interfaces';
import type FirebaseFirestore from 'firebase/firestore';
import type { PrivateAct, PublicAct } from '@sda/interfaces/act';
import type { Beacon } from '@sda/interfaces/beacon';
import type { Badge, Category, Country } from '@sda/interfaces/category';
import type { Message } from '@sda/interfaces/message';
import type { Payout } from '@sda/interfaces/payout';
import type { Permission } from '@sda/interfaces/permission';
import type { Request } from '@sda/interfaces/request';
import type { IPAddress } from '@sda/interfaces/setting/ipaddress';
import type { ExchangeRate } from '@sda/interfaces/setting/exchangerate';
import type { EmailTemplate } from '@sda/interfaces/setting/emailtemplate';
import type { PrivateReview, PublicReview } from '@sda/interfaces/review';
import type { Thread } from '@sda/interfaces/thread';
import type { PrivateTicket, PublicTicket } from '@sda/interfaces/ticket';
import type { PrivateUser, PublicUser } from '@sda/interfaces/user';
import type { CheckoutSession, Order } from '@sda/interfaces/order';
import type { PrivateShow, PublicShow } from '@sda/interfaces/show';
import type { PrivateVenue, PublicVenue } from '@sda/interfaces/venue';
import type { Invitation } from '@sda/interfaces/invitation';
import type { Email } from '@sda/interfaces/queue/email';
import type { Job } from '@sda/interfaces/queue/job';
import type { UserDelete } from '@sda/interfaces/queue/userdelete';
import type { ShowDelete } from '@sda/interfaces/queue/showdelete';
import type { Payout as Payout_Queue } from '@sda/interfaces/queue/payout';
import type { NotificationEmail } from '@sda/interfaces/queue/threadnotificationemail';
import type { Settlement } from '@sda/interfaces/queue/settlement';
import type { Configuration } from '@sda/interfaces/setting/homepage';
import type { EnvironmentVariables } from '@sda/interfaces/setting';
import type { Product } from '@sda/interfaces/product';
import type { ProfileDelete } from '@sda/interfaces/queue/profiledelete';
import type { QRCode } from '@sda/interfaces/queue/qrcode';

export class BaseHelpers {
  client: FirebaseFirestore.Firestore;

  clientData: FirestoreClientData;

  instanceType: FirestoreInstanceTypes;

  methods: FirestoreClientData['methods'];

  root = 'DataTypes';

  Schema: {
    [collectionName: string]: FirebaseFirestore.CollectionReference<any>;
  };

  constructor(props: FirestoreClientData) {
    this.client = props.methods.getFirestore(props.app);
    this.clientData = props;
    this.methods = props.methods;
    this.instanceType = props.instanceType;

    this.Schema = {
      // Acts
      PrivateActs: this.getSchema<PrivateAct>('Acts/PrivateActs'),
      PublicActs: this.getSchema<PublicAct>('Acts/PublicActs'),

      // Beacons
      PublicBeacons: this.getSchema<Beacon>('Beacons/PublicBeacons'),

      // Categories
      Categories_Badges: this.getSchema<Badge>('Categories/Badges'),
      Categories_Days: this.getSchema<Category>('Categories/Days'),
      Categories_Countries: this.getSchema<Country>('Categories/Countries'),
      Categories_Months: this.getSchema<Category>('Categories/Months'),
      Categories_ActTypes: this.getSchema<Category>('Categories/ActTypes'),
      Categories_DayPeriods: this.getSchema<Category>('Categories/DayPeriods'),
      Categories_MusicTypes: this.getSchema<Category>('Categories/MusicTypes'),
      Categories_ShowSalesRestrictions: this.getSchema<Category>('Categories/ShowSalesRestrictions'),
      Categories_ActStatuses: this.getSchema<Category>('Categories/ActStatuses'),
      Categories_HostStatuses: this.getSchema<Category>('Categories/HostStatuses'),
      Categories_Genres: this.getSchema<Category>('Categories/Genres'),
      Categories_VenueTypes: this.getSchema<Category>('Categories/VenueTypes'),
      Categories_VenueSizes: this.getSchema<Category>('Categories/VenueSizes'),
      Categories_ShowStatuses: this.getSchema<Category>('Categories/ShowStatuses'),
      Categories_Disabilities: this.getSchema<Category>('Categories/Disabilities'),
      Categories_Genders: this.getSchema<Category>('Categories/Genders'),
      Categories_Identities: this.getSchema<Category>('Categories/Identities'),
      Categories_Sexualities: this.getSchema<Category>('Categories/Sexualities'),

      // Feedback
      Feedback: this.getSchema<Feedback>('Feedback/Feedback'),

      // Invitations
      PrivateInvitations: this.getSchema<Invitation>('Invitations/PrivateInvitations'),

      // Links
      PublicLinks: this.getSchema<Link>('Links/PublicLinks'),

      // Messages
      PrivateMessages: this.getSchema<Message>('Messages/PrivateMessages'),

      // Notifications
      PublicNotifications: this.getSchema<Notification>('Notifications/PublicNotifications'),

      // Orders
      PrivateOrders: this.getSchema<Order>('Orders/PrivateOrders'),
      PrivateCheckoutSessions: this.getSchema<CheckoutSession>('Orders/PrivateCheckoutSessions'),

      // Payouts
      PrivatePayouts: this.getSchema<Payout>('Payouts/PrivatePayouts'),

      // Permissions
      Permissions: this.getSchema<Permission>('Permissions/Permissions'),

      // Profiles
      PublicProfiles: this.getSchema<PublicPendingProfile>('Profiles/PublicProfiles'),
      PrivateProfiles: this.getSchema<PrivatePendingProfile>('Profiles/PrivateProfiles'),

      // Queues
      AnalyticsQueue: this.getSchema('Queues/PrivateAnalytics'),
      EmailsQueue: this.getSchema<Email>('Queues/PrivateEmails'),
      JobsQueue: this.getSchema<Job>('Queues/PrivateJobs'),
      PayoutsQueue: this.getSchema<Payout_Queue>('Queues/PrivatePayouts'),
      SettlementsQueue: this.getSchema<Settlement>('Queues/PrivateSettlements'),
      ShowDeletesQueue: this.getSchema<ShowDelete>('Queues/PrivateShowDeletes'),
      BeaconNotificationEmailsQueue: this.getSchema<BeaconNotificationEmail>('Queues/PrivateBeaconNotificationEmails'),
      ReviewNotificationEmailsQueue: this.getSchema<ReviewNotificationEmail>('Queues/PrivateReviewNotificationEmails'),
      ShowNotificationEmailsQueue: this.getSchema<ShowNotificationEmail>('Queues/PrivateShowNotificationEmails'),
      ThreadNotificationEmailsQueue: this.getSchema<NotificationEmail>('Queues/PrivateThreadNotificationEmails'),
      UserDeletesQueue: this.getSchema<UserDelete>('Queues/PrivateUserDeletes'),
      ProfileDeletesQueue: this.getSchema<ProfileDelete>('Queues/PrivateProfileDeletes'),
      QRCodesQueue: this.getSchema<QRCode>('Queues/PrivateQRCodes'),
      SpotifyLookupsQueue: this.getSchema<QRCode>('Queues/PrivateSpotifyLookups'),
      // Show Requests
      PrivateRequests: this.getSchema<Request>('Requests/PrivateRequests'),

      // Reviews
      PrivateReviews: this.getSchema<PrivateReview>('Reviews/PrivateReviews'),
      PublicReviews: this.getSchema<PublicReview>('Reviews/PublicReviews'),

      // Settings
      EmailTemplates: this.getSchema<EmailTemplate>('Settings/EmailTemplates'),
      ExchangeRates: this.getSchema<ExchangeRate>('Settings/ExchangeRates'),
      FunctionsSettings: this.getSchema<EnvironmentVariables>('Settings/FunctionsSettings'),
      HomePage: this.getSchema<Configuration>('Settings/HomePage'),
      HostingSettings: this.getSchema<EnvironmentVariables>('Settings/HostingSettings'),
      IPAddresses: this.getSchema<IPAddress>('Settings/IPAddresses'),

      // Shows
      PrivateShows: this.getSchema<PrivateShow>('Shows/PrivateShows'),
      PublicShows: this.getSchema<PublicShow>('Shows/PublicShows'),

      // Sitemaps
      Sitemap_Acts: this.getSchema<Sitemap>('Acts/Sitemap'),
      Sitemap_Beacons: this.getSchema<Sitemap>('Beacons/Sitemap'),
      Sitemap_Shows: this.getSchema<Sitemap>('Shows/Sitemap'),
      Sitemap_Venues: this.getSchema<Sitemap>('Venues/Sitemap'),

      // Threads
      PublicThreads: this.getSchema<Thread>('Threads/PublicThreads'),

      // Tickets
      PrivateTickets: this.getSchema<PrivateTicket>('Tickets/PrivateTickets'),
      PublicTickets: this.getSchema<PublicTicket>('Tickets/PublicTickets'),

      // Users
      PrivateUsers: this.getSchema<PrivateUser>('Users/PrivateUsers'),
      PublicUsers: this.getSchema<PublicUser>('Users/PublicUsers'),

      // Venues
      PrivateVenues: this.getSchema<PrivateVenue>('Venues/PrivateVenues'),
      PublicVenues: this.getSchema<PublicVenue>('Venues/PublicVenues'),

      // Products
      PublicProducts: this.getSchema<Product>('Products/PublicProducts'),
    };
  }

  withConverter = <T>(collection: FirebaseFirestore.CollectionReference<any>): FirebaseFirestore.CollectionReference<T> => {
    const ref = collection.withConverter({
      fromFirestore: (snap: FirebaseFirestore.QueryDocumentSnapshot) => snap.data() as T,
      toFirestore: (data: T & SetDataType) => data,
    });

    return ref;
  };

  getSchema = <T>(collectionPath: string): FirebaseFirestore.CollectionReference => {
    const pathParts = collectionPath.split('/');

    return this.withConverter<T>(this.methods.collection(this.client, this.root, ...pathParts));
  };

  /**
   * Retrieve the current timestamp for the server
   * @return {FirebaseFirestore.Timestamp}
   */
  timestamp = (): FirebaseFirestore.Timestamp => this.methods.serverTimestamp() as FirebaseFirestore.Timestamp;

  /**
    * Returns a special value that can be used with set() or update() that tells the server
    * to union the given elements with any array value that already exists on the server
    * @return {FirebaseFirestore.FieldValue sentinel}
    * @param value
    */
  arrayUnion = (...value: any[]): any[] => this.methods.arrayUnion(...value) as any;

  /**
    * Returns a special value that can be used with set() or update() that tells the server
    * to remove the given elements from any array value that already exists on the server
    * @return {FirebaseFirestore.FieldValue sentinel}
    * @param value
    */
  arrayRemove = (...value: any[]): any[] => this.methods.arrayRemove(...value) as any;

  /**
    * Returns a special value that can be used with set() or update() that tells the server
    * to increment the field by the stated amount
    * @param  {number} amount
    * @return {number}
    */
  incrementBy = (amount: number): number => this.methods.increment(amount) as any;

  /**
    * Convenience method - Returns a special value that can be used with set() or update()
    * that tells the server to decrement the field by the stated amount
    * @param  {number} amount
    * @return {number}
    */
  decrementBy = (amount: number): number => this.methods.increment(Math.round(-1 * amount)) as any;

  /**
    * Create a GeoPoint reference for the specified latitude and longitude
    * @param  {number} lat
    * @param  {number} lng
    * @return {FirebaseFirestore.GeoPoint}
    */
  geopoint = (lat: number, lng: number): FirebaseFirestore.GeoPoint => new this.methods.GeoPoint(lat, lng);

  /**
   * Start and run a transaction
   * Returns any data that is returned in the callback
   * @param  {Function} callback
   * @return {any}
   */
  runTransaction = async <T>(callback: () => Promise<T>): Promise<T> => {
    if (TransactionManager.exists()) {
      return callback();
    }

    try {
      const result = await this.methods.runTransaction(this.client, (t): Promise<T> => {
        TransactionManager.set(t);

        return callback();
      });

      TransactionManager.unset();

      return result;
    } catch (e) {
      TransactionManager.unset();

      throw new Error(e);
    }
  };

  /**
   * Generates a new Transaction object that allows various operations to be done within a single Firestore transaction
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {FirebaseFirestore.Transaction} t
   * @return {Transaction}
   */
  getTransaction = (collection: FirebaseFirestore.CollectionReference, t: FirebaseFirestore.Transaction): Transaction => {
    const transaction = new Transaction(collection, t, this.methods);

    return transaction;
  };

  /**
   * Build a query constraint array based on the options provided
   * @param  {QueryOptions} options
   * @return {FirebaseFirestore.QueryConstraint[]}
   */
  getQueryConstraints = <T = unknown>(options: QueryOptions<T>, query?: QueryObject<T> | QueryObject<T>[]): FirebaseFirestore.QueryConstraint[] => {
    const {
      orderBy, order = 'asc', limit, startAt,
      limitToLast, startAfter, endBefore,
    } = options;

    let queryConstraints: FirebaseFirestore.QueryConstraint[] = [];

    if (!isEmpty(query)) {
      const whereArray = Array.isArray(query) ? query : [query];

      let i = 0;
      let len = whereArray.length;

      for (; i < len; i += 1) {
        const { attribute, operator = '==', value } = whereArray[i];

        queryConstraints.push(this.methods.where(`${attribute as string}`, operator, value));
      }
    }

    if (orderBy) {
      queryConstraints.push(this.methods.orderBy(orderBy, order));
    }

    if (limit) {
      queryConstraints.push(this.methods.limit(limit));
    }

    if (startAt) {
      queryConstraints.push(this.methods.startAt(startAt));
    }

    if (limitToLast) {
      queryConstraints.push(this.methods.limitToLast(limitToLast));
    }

    if (startAfter) {
      queryConstraints.push(this.methods.startAfter(startAfter));
    }

    if (endBefore) {
      queryConstraints.push(this.methods.endBefore(endBefore));
    }

    return queryConstraints;
  };

  /**
   * Generates a new Batch object that allows for individual records to be added to the collection
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @return {Batch}
   */
  getBatch = <T>(collection: FirebaseFirestore.CollectionReference<T>): Batch<T> => new Batch<T>(
    this.client,
    collection,
    this.timestamp,
    this.methods,
  );

  /**
   * Retrieve a document's subcollection
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @param  {string} collectionName
   * @return {FirebaseFirestore.CollectionReference}
   */
  getCollection = <T extends FirebaseFirestore.DocumentData>(
    collection: FirebaseFirestore.CollectionReference<T>,
    id: string,
    collectionName: string,
  ): FirebaseFirestore.CollectionReference<T> => this.withConverter<T>(this.methods.collection(this.client, collection.path, id, collectionName));

  getNextId = (collection: FirebaseFirestore.CollectionReference<any>): string => {
    const ref = this.methods.doc(collection);

    return ref.id;
  };

  //* CRUD Operations

  hasRecord = async (collection: FirebaseFirestore.CollectionReference, recordId: string): Promise<boolean> => {
    const documentReference = this.methods.doc(this.client, collection.path, recordId);
    const object = await this.methods.getDoc(documentReference);

    return firestoreDocumentExists(object);
  };

  /**
   * Retrieves the requested document reference
   * @param  {CollectionReference} collection
   * @param  {string} id
   * @return {FirebaseFirestore.DocumentReference}
   */
  getDocumentReference = <T = any>(collection: FirebaseFirestore.CollectionReference<T>, id?: string): FirebaseFirestore.DocumentReference<T> => {
    const reference = this.methods.doc(collection, id && `${id}`);

    return reference;
  };

  /**
   * Generic method to get record from Firestore collection by ID.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @return {Promise<any>}
   */
  getRecord = async <T>(
    collection: FirebaseFirestore.CollectionReference,
    id: string,
    useTransaction = false,
  ): Promise<T & FetchDataType> => {
    if (useTransaction && TransactionManager.exists()) {
      return this.getTransaction(collection, TransactionManager.get()).get(id);
    }

    const documentReference = this.methods.doc(this.client, collection.path, `${id}`);
    const snap = await this.methods.getDoc(documentReference);

    const exists = firestoreDocumentExists(snap);

    if (exists) {
      return {
        ...snap.data(),
        id: snap.id,
      } as T & FetchDataType;
    }

    return null;
  };

  /**
   * Generic counting method. To be used in SDK.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} orderBy
   * @return {Promise<any>}
   */
  countRecords = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    options: QueryOptions,
  ): Promise<number> => {
    const queryConstraints = this.getQueryConstraints(options);

    try {
      const query = this.methods.query(collection, ...queryConstraints);
      const snap = await this.methods.getCountFromServer(query);

      return snap.data().count;
    } catch (e) {
      console.log('NOT GOOD');
      console.error(e);
      return null;
    }
  };

  /**
   * Generic record listing method. To be used in SDK.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} orderBy
   * @return {Promise<any>}
   */
  listRecords = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    options: QueryOptions,
    useTransaction = false,
  ): Promise<GenericList<T & FetchDataType>> => {
    if (useTransaction && TransactionManager.exists()) {
      if (this.instanceType === FirestoreInstanceTypes.INSTANCE_TYPE_CLIENT) {
        console.log('Tried running a "listBy" call in a transaction');
      } else {
        return this.getTransaction(collection, TransactionManager.get()).list<T>(options || {});
      }
    }

    let data: GenericList<T & FetchDataType> = {};
    const queryConstraints = this.getQueryConstraints(options);

    try {
      const query = this.methods.query(collection, ...queryConstraints);
      const snap = await this.methods.getDocs(query);

      snap.forEach((doc) => {
        const exists = firestoreDocumentExists(doc);

        if (exists) {
          const item = {
            ...doc.data(),
            id: doc.id,
          } as T & FetchDataType;

          data[doc.id] = item;
        }
      });

      if (Object.keys(data).length === 0) {
        return null;
      }

      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  /**
   * Generic method to get one or more records from Firestore collection by attribute
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {any|any[]} query
   * @param  {number} limit
   * @return {Promise<any>}
   */
  listRecordsBy = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    query: QueryObject<T> | QueryObject<T>[],
    options: QueryOptions<T> = {},
    useTransaction = false,
  ): Promise<GenericList<T & FetchDataType>> => {
    if (useTransaction && TransactionManager.exists()) {
      if (this.instanceType === FirestoreInstanceTypes.INSTANCE_TYPE_CLIENT) {
        console.log('Tried running a "listBy" call in a transaction');
      } else {
        return this.getTransaction(collection, TransactionManager.get()).listBy<T>(query, options || {});
      }
    }

    let data: GenericList<T & FetchDataType> = {};
    const queryConstraints = this.getQueryConstraints<T>(options, query);

    const queryRef = this.methods.query(collection, ...queryConstraints);
    const snap = await this.methods.getDocs(queryRef);

    snap.forEach((doc) => {
      const exists = firestoreDocumentExists(doc);

      if (exists) {
        const item = {
          ...doc.data(),
          id: doc.id,
        };

        data[doc.id] = item;
      }
    });

    if (isEmpty(data)) {
      return null;
    }

    return data;
  };

  /**
   * Generic method to set Firestore record in collection by ID.
   * NOTE: This will remove all data in the record and replace it with the new data.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @param  {any} data
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  setRecord = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    id: string,
    data: Partial<T & SetDataType>,
  ): Promise<FirebaseFirestore.DocumentReference | FirebaseFirestore.Transaction> => {
    const record = {
      ...data,
      createdAt: (data.createdAt || this.timestamp()),
      updatedAt: (data.updatedAt || this.timestamp()),
    } as T & SetDataType;

    if (TransactionManager.exists()) {
      return this.getTransaction(collection, TransactionManager.get()).set(id, record);
    }

    const reference = this.methods.doc(collection, `${id}`);
    await this.methods.setDoc(reference, record);

    return reference;
  };

  /**
   * Generic method to set Firestore record in collection by ID.
   * NOTE: This will remove all data in the record and replace it with the new data.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @param  {any} data
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  addRecord = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    data: Partial<T & SetDataType>,
    id?: string,
  ): Promise<FirebaseFirestore.DocumentReference> => {
    const timestamp = this.timestamp();

    const record = {
      ...data,
      createdAt: data.createdAt || timestamp,
      updatedAt: data.updatedAt || data.createdAt || timestamp,
    } as T & SetDataType;

    if (TransactionManager.exists()) {
      return this.getTransaction(collection, TransactionManager.get()).add(record, id);
    }

    if (!id) {
      return this.methods.addDoc(collection, record);
    }

    const reference = this.methods.doc(collection, `${id}`);
    await this.methods.setDoc(reference, record);

    return reference;
  };

  /**
   * Generic method to update or create a Firestore doc in a collection by ID.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @param  {any} data
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  updateCreateRecord = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    id: string,
    data: Partial<T & SetDataType>,
  ): Promise<FirebaseFirestore.DocumentReference> => {
    const docRef = this.methods.doc(collection, `${id}`);
    const snap = await this.methods.getDoc(docRef);

    const timestamp = this.timestamp();

    const exists = firestoreDocumentExists(snap);

    if (exists) {
      await this.methods.updateDoc(docRef, { ...data, updatedAt: timestamp } as any);
    } else {
      await this.methods.setDoc(docRef, { ...data, createdAt: timestamp, updatedAt: timestamp } as T & SetDataType);
    }

    return docRef;
  };

  /**
   * Generic method to update Firestore record in collection by ID.
   * NOTE: This will only replace the fields that are included in @data. Anything else in the object will be
   * left as is (as opposed to "setRecord" which will completely replace the data with a new object).
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @param  {any} data
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  updateRecord = async <T>(
    collection: FirebaseFirestore.CollectionReference<T>,
    id: string,
    data: Partial<T & SetDataType>,
  ): Promise<FirebaseFirestore.DocumentReference | FirebaseFirestore.Transaction> => {
    const updateData = {
      updatedAt: this.timestamp(),
      ...data,
    };

    if (TransactionManager.exists()) {
      return this.getTransaction(collection, TransactionManager.get()).update(id, updateData);
    }

    const reference = this.methods.doc(collection, `${id}`);
    await this.methods.updateDoc(reference, updateData as any);

    return reference;
  };

  /**
   * Generic method to delete record in Firestore collection by ID.
   * @param  {FirebaseFirestore.CollectionReference} collection
   * @param  {string} id
   * @return {Promise<void}
   */
  deleteRecord = async (
    collection: FirebaseFirestore.CollectionReference,
    id: string,
  ): Promise<void | FirebaseFirestore.Transaction> => {
    if (TransactionManager.exists()) {
      return this.getTransaction(collection, TransactionManager.get()).delete(id);
    }

    const reference = this.methods.doc(collection, `${id}`);
    const result = await this.methods.deleteDoc(reference);

    return result;
  };

  /* stubs */

  addBatch = async <T = any>(...args: any[]): Promise<void | T> => {
    console.log('This is a stub', args);

    await Promise.resolve(true);
  };

  updateBatch = async <T = any>(...args: any[]): Promise<void | T> => {
    console.log('This is a stub', args);

    await Promise.resolve(true);
  };

  setBatch = async <T = any>(...args: any[]): Promise<void | T> => {
    console.log('This is a stub', args);

    await Promise.resolve(true);
  };

  listRecordsIn = async <T = any>(...args: any[]): Promise<any | T> => { // eslint-disable-line @typescript-eslint/no-redundant-type-constituents
    console.log('This is a stub', args);

    await Promise.resolve(true);
  };

  watchRecord = <T, S>(...args: any[]): any | T | S => { // eslint-disable-line @typescript-eslint/no-redundant-type-constituents
    console.log('This is a stub', args);
  };
}
